import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import moment from "moment";
import { initReactI18next } from "react-i18next";
import en from "./locales/en.json";
import es from "./locales/es.json";
import kk from "./locales/kk.json";
import ru from "./locales/ru.json";
import {
    fallbackUiLanguage,
    resolveUiLanguage,
    UI_LANGUAGE_STORAGE_KEY,
    uiLanguages
} from "./uiLanguages";

/**
 * Interface localization.
 *
 * One namespace (`translation`) with nested keys — the bundles are small enough
 * that splitting them would only add lazy-loading machinery. Bundles are
 * imported statically so the first render already has its strings: this is an
 * admin tool behind a login, not a page whose bundle size decides a bounce rate.
 *
 * The chosen language is remembered in `localStorage` under `uiLanguage` and,
 * on a first visit, detected from the browser. Anything we do not ship a bundle
 * for falls back to English.
 */
export const defaultNS = "translation";

i18n.use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: {
            en: { translation: en },
            kk: { translation: kk },
            ru: { translation: ru },
            es: { translation: es }
        },
        supportedLngs: uiLanguages.map(language => language.code),
        // `ru-RU` and friends resolve to the `ru` bundle instead of falling all
        // the way back to English.
        load: "languageOnly",
        fallbackLng: fallbackUiLanguage.code,
        defaultNS,
        interpolation: {
            // React escapes for us; double-escaping would render `&quot;`.
            escapeValue: false
        },
        detection: {
            order: ["localStorage", "navigator"],
            lookupLocalStorage: UI_LANGUAGE_STORAGE_KEY,
            caches: ["localStorage"]
        },
        react: {
            useSuspense: false
        }
    });

/** Keeps `moment` and `<html lang>` in step with the interface language. */
function applyLanguage(code: string): void {
    const language = resolveUiLanguage(code);
    moment.locale(language.moment);
    document.documentElement.lang = language.code;
}

applyLanguage(i18n.language);
i18n.on("languageChanged", applyLanguage);

export default i18n;
